import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Edit, Trash2, ArrowLeft, Upload, User, Loader2, Shield, Lock, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const ManageLeaders = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLeader, setEditingLeader] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name_en: '', name_bn: '',
    designation_en: '', designation_bn: '',
    bio_en: '', bio_bn: '',
    image_url: '', order: 1,
  });

  useEffect(() => { fetchLeaders(); }, []);

  const fetchLeaders = async () => {
    try {
      const res = await fetch('/api/leaders/');
      if (res.ok) setLeaders(await res.json());
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const resetForm = () => {
    setFormData({ name_en: '', name_bn: '', designation_en: '', designation_bn: '', bio_en: '', bio_bn: '', image_url: '', order: 1 });
    setEditingLeader(null);
    setShowForm(false);
  };

  const handleEdit = (leader) => {
    setFormData({
      name_en: leader.name?.en || '', name_bn: leader.name?.bn || '',
      designation_en: (leader.designation || leader.position)?.en || '',
      designation_bn: (leader.designation || leader.position)?.bn || '',
      bio_en: leader.bio?.en || '', bio_bn: leader.bio?.bn || '',
      image_url: leader.image_url || '', order: leader.order || 1,
    });
    setEditingLeader(leader);
    setShowForm(true);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch('/api/leaders/upload-photo', { method: 'POST', body: fd });
      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({ ...prev, image_url: data.url }));
        toast.success('Photo uploaded!');
      } else { toast.error('Upload failed'); }
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    const body = {
      name: { en: formData.name_en, bn: formData.name_bn },
      position: { en: formData.designation_en, bn: formData.designation_bn },
      bio: { en: formData.bio_en, bn: formData.bio_bn },
      image_url: formData.image_url, order: parseInt(formData.order),
    };
    try {
      const url = editingLeader ? `/api/admin/leaders/${editingLeader._id}` : '/api/admin/leaders';
      const method = editingLeader ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      if (res.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin/login'); return; }
      if (res.ok) {
        toast.success(editingLeader ? 'Leader updated!' : 'Leader added!');
        resetForm();
        fetchLeaders();
      } else { toast.error('Error saving leader'); }
    } catch (e) { toast.error('Error saving leader'); }
  };

  const handleDelete = async (leader) => {
    setDeleteConfirm(leader);
    setDeletePassword('');
  };

  const confirmDelete = async () => {
    if (!deletePassword) { toast.error('Please enter your admin password'); return; }
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    setDeleting(true);
    try {
      const res = await fetch('/api/admin/confirm-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password: deletePassword, type: 'leaders', id: deleteConfirm._id }),
      });
      if (res.ok) {
        toast.success('Leader deleted!');
        setLeaders(prev => prev.filter(l => l._id !== deleteConfirm._id));
        setDeleteConfirm(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Delete failed');
      }
    } catch (e) { toast.error('Delete failed'); }
    finally { setDeleting(false); }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current || !passwordForm.newPass) { toast.error('All fields required'); return; }
    if (passwordForm.newPass !== passwordForm.confirm) { toast.error('New passwords do not match'); return; }
    if (passwordForm.newPass.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    setChangingPassword(true);
    try {
      const res = await fetch('/api/admin/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: passwordForm.current, new_password: passwordForm.newPass }),
      });
      if (res.ok) {
        toast.success('Password changed successfully!');
        setShowChangePassword(false);
        setPasswordForm({ current: '', newPass: '', confirm: '' });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to change password');
      }
    } catch (e) { toast.error('Failed to change password'); }
    finally { setChangingPassword(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="manage-leaders-page">
      <div className="bg-party-red text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Manage Leaders ({leaders.length})</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowChangePassword(true)} variant="ghost" size="sm" className="text-white hover:bg-white/20" data-testid="change-password-btn">
            <KeyRound className="h-4 w-4 mr-1" /> Password
          </Button>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-white text-party-red hover:bg-gray-100" data-testid="add-leader-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Leader
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6">

        {/* Change Password Modal */}
        {showChangePassword && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="change-password-modal">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-party-red" /> Change Admin Password</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Current Password</label>
                  <Input type="password" value={passwordForm.current} onChange={e => setPasswordForm(p => ({...p, current: e.target.value}))} placeholder="Enter current password" data-testid="current-password-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">New Password</label>
                  <Input type="password" value={passwordForm.newPass} onChange={e => setPasswordForm(p => ({...p, newPass: e.target.value}))} placeholder="Min 6 characters" data-testid="new-password-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Confirm New Password</label>
                  <Input type="password" value={passwordForm.confirm} onChange={e => setPasswordForm(p => ({...p, confirm: e.target.value}))} placeholder="Re-enter new password" data-testid="confirm-password-input" />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleChangePassword} disabled={changingPassword} className="bg-party-red hover:bg-party-red-600" data-testid="save-password-btn">
                    {changingPassword ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}
                    {changingPassword ? 'Saving...' : 'Change Password'}
                  </Button>
                  <Button variant="outline" onClick={() => { setShowChangePassword(false); setPasswordForm({ current: '', newPass: '', confirm: '' }); }}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
            <Card className="w-full max-w-md border-t-4 border-t-red-500">
              <CardHeader>
                <CardTitle className="text-red-600 flex items-center gap-2"><Trash2 className="h-5 w-5" /> Confirm Delete</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">You are about to delete <strong>{t(deleteConfirm.name) || t(deleteConfirm.position || deleteConfirm.designation)}</strong>. Enter your admin password to confirm:</p>
                <Input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} placeholder="Enter admin password" autoFocus data-testid="delete-password-input"
                  onKeyDown={e => { if (e.key === 'Enter') confirmDelete(); }} />
                <div className="flex gap-2">
                  <Button variant="destructive" onClick={confirmDelete} disabled={deleting} data-testid="confirm-delete-btn">
                    {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    {deleting ? 'Deleting...' : 'Delete'}
                  </Button>
                  <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {showForm && (
          <Card className="mb-6 border-t-4 border-t-party-red" data-testid="leader-form">
            <CardHeader><CardTitle>{editingLeader ? 'Edit Leader' : 'Add New Leader'}</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Photo Upload Section */}
                <div className="flex items-center gap-6 pb-4 border-b">
                  <div className="w-28 h-28 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 border-2 border-gray-200">
                    {formData.image_url ? (
                      <img src={formData.image_url} alt="Leader" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><User className="h-12 w-12 text-gray-300" /></div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Leader Photo</p>
                    <div className="flex gap-2 items-center">
                      <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()} data-testid="upload-photo-btn">
                        {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                        {uploading ? 'Uploading...' : 'Upload Photo'}
                      </Button>
                      <span className="text-xs text-gray-400">or paste URL below</span>
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                    <Input placeholder="Image URL" value={formData.image_url} onChange={e => setFormData({...formData, image_url: e.target.value})} className="text-sm" data-testid="leader-image-url" />
                  </div>
                </div>

                {/* Name & Designation */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input placeholder="Name (English) *" value={formData.name_en} onChange={e => setFormData({...formData, name_en: e.target.value})} required data-testid="leader-name-en" />
                  <Input placeholder="Name (Bengali)" value={formData.name_bn} onChange={e => setFormData({...formData, name_bn: e.target.value})} data-testid="leader-name-bn" />
                  <Input placeholder="Designation (English) *" value={formData.designation_en} onChange={e => setFormData({...formData, designation_en: e.target.value})} required data-testid="leader-designation-en" />
                  <Input placeholder="Designation (Bengali)" value={formData.designation_bn} onChange={e => setFormData({...formData, designation_bn: e.target.value})} data-testid="leader-designation-bn" />
                </div>

                {/* Bio */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Textarea placeholder="Bio (English)" value={formData.bio_en} onChange={e => setFormData({...formData, bio_en: e.target.value})} rows={4} data-testid="leader-bio-en" />
                  <Textarea placeholder="Bio (Bengali)" value={formData.bio_bn} onChange={e => setFormData({...formData, bio_bn: e.target.value})} rows={4} data-testid="leader-bio-bn" />
                </div>

                {/* Order */}
                <div className="w-32">
                  <Input type="number" placeholder="Display Order" value={formData.order} onChange={e => setFormData({...formData, order: e.target.value})} min="1" data-testid="leader-order" />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="submit" className="bg-party-red hover:bg-party-red-600" data-testid="leader-submit-btn">{editingLeader ? 'Update Leader' : 'Create Leader'}</Button>
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-party-red mx-auto"></div></div>
        ) : leaders.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No leaders found. Click "Add Leader" to get started.</div>
        ) : (
          <div className="space-y-3">
            {leaders.map((leader) => {
              const hasImage = leader.image_url && leader.image_url.length > 5;
              const hasName = t(leader.name)?.trim();
              return (
                <Card key={leader._id} className="hover:shadow-md transition-shadow" data-testid={`leader-row-${leader._id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 border">
                      {hasImage ? (
                        <img src={leader.image_url} alt={hasName || 'Leader'} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><User className="h-8 w-8 text-gray-300" /></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg truncate">{hasName || <span className="text-gray-400 italic">Name not set</span>}</h3>
                      <p className="text-party-red text-sm">{t(leader.designation || leader.position)}</p>
                      <p className="text-gray-400 text-xs">Order: {leader.order || '—'}</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(leader)} data-testid={`edit-leader-${leader._id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(leader)} data-testid={`delete-leader-${leader._id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageLeaders;
